import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { SortableHeader, PAGE_SIZE } from '../SortableTable';
import { inputCls } from '../shared/FormKit';
import { fetchJSON } from '../../utils/api';
import { rowExpandPanel } from './constants';

// The one workflow list page (docs/workflow-ui.md §4.1). Every workflow list
// renders through this shell: back link, title + New CTA, debounced search,
// caller-supplied filters, sortable server-paginated table, "Hide completed",
// unified pagination/empty/loading states and whole-row navigation.
//
// Props:
//   title, subtitle       — page heading; subtitle string (caller picks persona copy)
//   newTo, newLabel       — CTA (hidden when canCreate is false)
//   apiPath               — list endpoint returning { data, total }
//   columns               — [{ key, label, sortKey?, render(row, { expanded, toggle }), className?, cellTitle?(row) }]
//                           omit sortKey for a plain header
//   expandRow(row)        — optional: content of the row's expanded sub-row, or
//                           null when that row has nothing to expand. A column
//                           puts the toggle in its cell via render's second arg;
//                           the sub-row itself does not navigate.
//   rowTo(row)            — detail route; first column should also <Link> there
//   rowNewTab             — true opens the row's detail route in a new tab
//   searchPlaceholder
//   toolbarTop            — optional row above the search box (Status chips +
//                           view toggle, laid out like the Grouped board's bar)
//   filters               — extra toolbar controls (parent-owned state)
//   extraParams           — object merged into the query string; changing it refetches + resets page
//   defaultSort           — { key, direction } initial server sort
//   width                 — 'wide' (max-w-7xl, default) | 'mid' (max-w-5xl)
//   emptyText             — defaults to the unified "No requests found"
//   showHideCompleted     — false for lists with no lifecycle to complete
//                           (the Management record lists): a
//                           checkbox that filters on nothing is worse than no
//                           checkbox
//   embedded              — true when the shell sits INSIDE another page
//                           (My Records' Engagements tab): skips
//                           the back link + title/CTA header and the page
//                           padding/max-width, keeping only toolbar + table +
//                           pagination. The host page owns the heading.
const THEAD_CLS = 'px-4 py-2 text-xs font-semibold text-slate-500 uppercase';

const WorkflowListShell = ({
  title,
  subtitle,
  backTo = '/workflow',
  backLabel = 'Workflow',
  newTo,
  newLabel = 'New Request',
  canCreate = true,
  apiPath,
  columns,
  rowTo,
  rowNewTab = false,
  searchPlaceholder = 'Search…',
  toolbarTop = null,
  filters = null,
  extraParams = null,
  defaultSort = null,
  width = 'wide',
  emptyText = 'No requests found',
  showHideCompleted = true,
  embedded = false,
  expandRow = null,
}) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hideCompleted, setHideCompleted] = useState(showHideCompleted);
  const [sortConfig, setSortConfig] = useState(defaultSort);
  // Row ids whose expandRow sub-row is open; cleared whenever a new page loads.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleRow = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Stable dependency for the caller-owned filter object.
  const extraKey = useMemo(() => JSON.stringify(extraParams || {}), [extraParams]);

  const requestSort = (key) => {
    setPage(1);
    setSortConfig((prev) =>
      prev && prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' });
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, hideCompleted, extraKey]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (hideCompleted) params.set('notCompleted', 'true');
      Object.entries(JSON.parse(extraKey)).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') params.set(k, String(v));
      });
      if (sortConfig?.key) { params.set('sort', sortConfig.key); params.set('dir', sortConfig.direction); }
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      const data = await fetchJSON(`${apiPath}?${params.toString()}`);
      setRows(data.data || []);
      setTotal(data.total || 0);
      setExpanded(new Set());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [apiPath, debouncedSearch, hideCompleted, extraKey, sortConfig, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const widthCls = width === 'mid' ? 'max-w-5xl' : 'max-w-7xl';

  return (
    <div className={embedded ? '' : `p-4 sm:p-6 ${widthCls} mx-auto`}>
      {!embedded && (
        <>
          <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" /> {backLabel}</Link>
          <div className="mt-2 mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h1>
              {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
            </div>
            {canCreate && newTo && (
              <Link
                to={newTo}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" /> {newLabel}
              </Link>
            )}
          </div>
        </>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4">
        {toolbarTop && (
          <div className="flex flex-wrap items-center gap-2 pb-3 mb-3 border-b border-slate-100">{toolbarTop}</div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className={`${inputCls} pl-9`}
            />
          </div>
          {filters}
          {showHideCompleted && (
            <label className="flex items-center gap-2 text-sm text-slate-700 whitespace-nowrap">
              <input type="checkbox" checked={hideCompleted} onChange={(e) => setHideCompleted(e.target.checked)} />
              Hide completed
            </label>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                {columns.map((c) => (
                  <SortableHeader
                    key={c.key}
                    label={c.label}
                    sortKey={c.sortKey}
                    sortable={!!c.sortKey}
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    className={THEAD_CLS}
                  />
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-slate-500">{emptyText}</td></tr>
              ) : rows.map((r) => {
                const isOpen = expanded.has(r.id);
                const panel = expandRow && isOpen ? expandRow(r) : null;
                const ctx = { expanded: isOpen, toggle: () => toggleRow(r.id) };
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      className={`hover:bg-slate-50 ${rowTo ? 'cursor-pointer' : ''}`}
                      onClick={rowTo ? () => (rowNewTab ? window.open(rowTo(r), '_blank', 'noopener') : navigate(rowTo(r))) : undefined}
                    >
                      {columns.map((c) => (
                        <td
                          key={c.key}
                          className={`px-4 py-2 ${c.className || 'text-slate-700'}`}
                          title={c.cellTitle ? c.cellTitle(r) : undefined}
                        >
                          {c.render(r, ctx)}
                        </td>
                      ))}
                    </tr>
                    {panel && (
                      <tr>
                        <td colSpan={columns.length} className={rowExpandPanel}>{panel}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm">
          <span className="text-slate-500">
            {total > 0
              ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`
              : '0 of 0'}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-slate-500">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowListShell;
